/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState, type ReactNode } from 'react';
import { Image, Text, View, type StyleProp, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SoftPressable } from '../ui/SoftPressable';
import { ticketColors } from '../theme/public-api';
import { ticketList } from './ticketList.styles';
import { ticketText } from './ticketText';

type StatusTone = 'ok' | 'bad' | 'warn' | 'muted';
type AvatarTone = 'gold' | 'bad' | 'muted';
type ChipTone = 'default' | 'warn' | 'bad';
export type TicketListRowTrailingPill = {
  label: string;
  tone: 'ok' | 'warn' | 'bad' | 'brand' | 'muted';
};

export type TicketListRowGroup = 'solo' | 'grouped' | 'groupedLast';

export type TicketListRowIconTone = 'muted' | 'brand' | 'bad' | 'ok';

type Props = {
  title: string;
  subtitle?: string | null;
  meta?: string | null;
  value?: string | null;
  /** Aclaración chica bajo `value` (ej. "de $48.500" en saldos parciales). */
  valueSub?: string | null;
  /** Tono del valor (proto .aq.bad/.warn/.ok — egreso/vence/ingreso). */
  valueTone?: 'bad' | 'warn' | 'ok' | null;
  /** Renderiza `value` como pill brand en vez de texto mono (beneficio de promo). */
  valueChip?: boolean;
  statusLabel?: string | null;
  statusTone?: StatusTone;
  /** Pills rellenas junto al status (pago, origen) — proto Compras/Gastos. */
  trailingPills?: TicketListRowTrailingPill[];
  /** Chip Square bajo el meta (proto Catálogo). */
  chipLabel?: string | null;
  chipTone?: ChipTone;
  inactive?: boolean;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
  /**
   * Estilo extra del Pressable interno (hit area). Usar para garantizar un
   * alto táctil mínimo (ej. `minHeight: 44`) sin cambiar el padding del row.
   */
  pressableStyle?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
  avatar?: { initials: string; tone?: AvatarTone } | null;
  /**
   * URL de imagen del ítem. Si el caller pasa la prop (incluso `null`), el slot
   * queda reservado con un placeholder para que todas las filas alineen igual.
   */
  leadingImage?: string | null;
  leadingImageResizeMode?: 'cover' | 'contain';
  /** Badge de ícono 32px a la izquierda (proto .aic — actividad de caja). */
  leadingIcon?: {
    name: React.ComponentProps<typeof Ionicons>['name'];
    tone?: TicketListRowIconTone;
  } | null;
  /** Texto link inline a la derecha antes del chevron (proto .see-all). */
  linkLabel?: string;
  showChevron?: boolean;
  footer?: ReactNode;
  group?: TicketListRowGroup;
};

const statusStyle = {
  ok: ticketText.statusOk,
  bad: ticketText.statusBad,
  warn: ticketText.statusWarn,
  muted: ticketText.statusMuted,
} as const;

const avatarToneStyle: Record<AvatarTone, { bg: string; fg: string }> = {
  gold: { bg: ticketColors.gold + '25', fg: ticketColors.gold },
  bad: { bg: ticketColors.bad + '20', fg: ticketColors.bad },
  muted: { bg: ticketColors.tile, fg: ticketColors.ink2 },
};

const chipBox = {
  default: ticketList.chip,
  warn: ticketList.chipWarn,
  bad: ticketList.chipBad,
} as const;

const chipTxt = {
  default: ticketList.chipText,
  warn: ticketList.chipTextWarn,
  bad: ticketList.chipTextBad,
} as const;

const trailingPillPalette: Record<
  TicketListRowTrailingPill['tone'],
  { bg: string; fg: string }
> = {
  ok: { bg: ticketColors.ok + '14', fg: ticketColors.ok },
  bad: { bg: ticketColors.bad + '14', fg: ticketColors.bad },
  warn: { bg: ticketColors.warn + '14', fg: ticketColors.warn },
  brand: { bg: ticketColors.brandSoft, fg: ticketColors.brandInk },
  muted: { bg: ticketColors.tile, fg: ticketColors.ink3 },
};

const leadingIconPalette: Record<
  TicketListRowIconTone,
  { bg: string; border: string; fg: string }
> = {
  muted: { bg: ticketColors.tile, border: ticketColors.border, fg: ticketColors.ink2 },
  brand: { bg: ticketColors.brandSoft, border: ticketColors.brandBorderSoft, fg: ticketColors.brandInk },
  bad: { bg: ticketColors.bad + '14', border: ticketColors.bad + '40', fg: ticketColors.bad },
  ok: { bg: ticketColors.ok + '14', border: ticketColors.ok + '40', fg: ticketColors.ok },
};

function TrailingPill({ pill }: { pill: TicketListRowTrailingPill }) {
  const palette = trailingPillPalette[pill.tone];
  return (
    <View style={[ticketList.trailingPill, { backgroundColor: palette.bg }]}>
      <Text style={[ticketList.trailingPillText, { color: palette.fg }]} numberOfLines={1}>
        {pill.label}
      </Text>
    </View>
  );
}

export function TicketListRow({
  title,
  subtitle,
  meta,
  value,
  valueSub,
  valueTone,
  valueChip = false,
  statusLabel,
  statusTone = 'muted',
  trailingPills,
  chipLabel,
  chipTone = 'default',
  inactive = false,
  onPress,
  style,
  pressableStyle,
  accessibilityLabel,
  testID,
  avatar,
  leadingImage,
  leadingImageResizeMode = 'cover',
  leadingIcon,
  linkLabel,
  showChevron = false,
  footer,
  group = 'solo',
}: Props) {
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    setImageError(false);
  }, [leadingImage]);

  const rowStyle =
    group === 'solo'
      ? ticketList.row
      : [ticketList.rowGrouped, group === 'groupedLast' && ticketList.rowGroupedLast];

  const body = (
    <View style={ticketList.rowInner}>
      {leadingImage !== undefined ? (
        leadingImage && !imageError ? (
          <Image
            source={{ uri: leadingImage }}
            style={ticketList.thumbnail}
            resizeMode={leadingImageResizeMode}
            accessibilityIgnoresInvertColors
            onError={() => setImageError(true)}
          />
        ) : (
          <View style={[ticketList.thumbnail, ticketList.thumbnailPlaceholder]}>
            <Ionicons name="image-outline" size={18} color={ticketColors.ink3} />
          </View>
        )
      ) : avatar ? (
        <View
          style={[
            ticketList.avatar,
            { backgroundColor: avatarToneStyle[avatar.tone ?? 'muted'].bg },
          ]}
        >
          <Text
            style={[
              ticketList.avatarText,
              { color: avatarToneStyle[avatar.tone ?? 'muted'].fg },
            ]}
          >
            {avatar.initials}
          </Text>
        </View>
      ) : leadingIcon ? (
        <View
          style={[
            ticketList.leadingIconBadge,
            {
              backgroundColor: leadingIconPalette[leadingIcon.tone ?? 'muted'].bg,
              borderColor: leadingIconPalette[leadingIcon.tone ?? 'muted'].border,
            },
          ]}
        >
          <Ionicons
            name={leadingIcon.name}
            size={15}
            color={leadingIconPalette[leadingIcon.tone ?? 'muted'].fg}
          />
        </View>
      ) : null}
      <View style={ticketList.main}>
        <Text style={ticketList.name} numberOfLines={2}>
          {title}
        </Text>
        {subtitle ? (
          <Text style={ticketList.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
        {meta ? (
          <Text style={ticketList.meta} numberOfLines={2}>
            {meta}
          </Text>
        ) : null}
        {chipLabel ? (
          <View style={chipBox[chipTone]}>
            <Text style={chipTxt[chipTone]}>{chipLabel}</Text>
          </View>
        ) : null}
      </View>
      {value || statusLabel || linkLabel || showChevron || (trailingPills?.length ?? 0) > 0 ? (
        <View style={ticketList.trail}>
          {value ? (
            valueChip ? (
              <View style={ticketList.valuePill}>
                <Text style={ticketList.valuePillText} numberOfLines={1}>
                  {value}
                </Text>
              </View>
            ) : (
              <Text
                style={[
                  ticketList.value,
                  valueTone === 'bad' && { color: ticketColors.bad },
                  valueTone === 'warn' && { color: ticketColors.warn },
                  valueTone === 'ok' && { color: ticketColors.ok },
                ]}
                numberOfLines={1}
              >
                {value}
              </Text>
            )
          ) : null}
          {valueSub ? (
            <Text style={ticketList.valueSub} numberOfLines={1}>
              {valueSub}
            </Text>
          ) : null}
          {statusLabel && !chipLabel ? (
            <Text style={statusStyle[statusTone]}>{statusLabel}</Text>
          ) : null}
          {trailingPills?.map((pill) => <TrailingPill key={pill.label} pill={pill} />)}
          {linkLabel ? (
            <Text style={ticketList.linkLabel} numberOfLines={1}>
              {linkLabel}
            </Text>
          ) : null}
          {showChevron ? (
            <Ionicons name="chevron-forward" size={16} color={ticketColors.ink3} />
          ) : null}
        </View>
      ) : null}
    </View>
  );

  if (onPress) {
    return (
      <View
        style={[
          rowStyle,
          { flexDirection: 'column', alignItems: 'stretch' },
          inactive && ticketList.rowInactive,
          style,
        ]}
        testID={testID}
      >
        <SoftPressable
          onPress={onPress}
          style={[
            footer ? ticketList.rowPadTop : ticketList.rowPad,
            group === 'solo' && ticketList.rowRadius,
            pressableStyle,
          ]}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel ?? title}
          pressScale={!footer}
          hoverLift={!footer}
        >
          {body}
        </SoftPressable>
        {footer ? <View style={ticketList.footerCard}>{footer}</View> : null}
      </View>
    );
  }

  return (
    <View
      style={[rowStyle, ticketList.rowPad, inactive && ticketList.rowInactive, style]}
      testID={testID}
    >
      {body}
      {footer ? <View style={ticketList.footer}>{footer}</View> : null}
    </View>
  );
}
